import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCategories } from '../../../hooks/useCategories';
import { useServices } from '../../../hooks/useServices';
import { useBranches } from '../../../hooks/useBranches';
import { useToast } from '../../../contexts/ToastContext';
import { Category } from '../../../types/api-responses';
import type { ServiceDetailsResponse } from '../../../types/api-responses';
import SearchBar from '../../../components/SearchBar/SearchBar';
import Button from '../../../components/Button/Button';
import ServiceCard from './ServiceCard/ServiceCard';
import ServiceControls from './ServiceControls/ServiceControls'
import DeleteModal from '../../../components/DeleteModal/DeleteModal';
import styles from './Services.module.css';
import AddEditService from './AddEditService/AddEditService';
import { resolveImageUrl } from '../../../utils/imageUrl';

// #1324: Fallback for broken/missing category thumbnails.
const DEFAULT_CATEGORY_IMAGE = './assets/images/default-service.png';
const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img.src.endsWith(DEFAULT_CATEGORY_IMAGE.replace('./', '/'))) return;
    img.src = DEFAULT_CATEGORY_IMAGE;
};

const Services: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingServiceId, setEditingServiceId] = useState<number | null>(null);
    // CR-2026-05-13 — Deep-link support: ?tab=categories opens the Categories
    // tab directly. Used by the My Business → Services empty-state CTA which
    // bounces owners here to set up categories before adding services.
    const [searchParams] = useSearchParams();
    const initialTap = searchParams.get('tab') === 'categories' ? 'categories' : 'services';
    const [activeTap, setActiveTap] = useState<'services' | 'categories'>(initialTap);
    const [businessCategories, setBusinessCategories] = useState<Category[]>([]);
    // #48: Live search for services and categories
    const [searchQuery, setSearchQuery] = useState('');

    // Delete modal state - now handles both services and categories
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<{ type: 'service' | 'category'; item: ServiceDetailsResponse | Category } | null>(null);

    // Category menu state
    const [openMenuId, setOpenMenuId] = useState<number | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const { getBusinessCategories, removeCategoryFromBusiness } = useCategories();
    const { getServices, services, deleteService, prependService } = useServices({ forOwner: true });
    const { getBranches } = useBranches();
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [branchMap, setBranchMap] = useState<Record<number, string>>({});

    const fetchCategories = useCallback(async () => {
        try {
            const businessCats = await getBusinessCategories();
            setBusinessCategories(businessCats.categories);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    }, [getBusinessCategories]);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [, , branchesRes] = await Promise.all([
                fetchCategories(),
                getServices(),
                getBranches()
            ]);
            if (branchesRes && Array.isArray(branchesRes.branches)) {
                const map = Object.fromEntries(branchesRes.branches.map(b => [b.id, b.name]));
                setBranchMap(map);
            } else {
                setBranchMap({});
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setIsLoading(false);
        }
    }, [fetchCategories, getServices, getBranches]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Handle click outside menu
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setOpenMenuId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleTapClick = (tap: 'services' | 'categories') => {
        setActiveTap(tap);
    };

    const handleAddService = () => {
        setEditingServiceId(null);
        setIsModalOpen(true);
    };

    const handleEditService = (serviceId: number) => {
        setEditingServiceId(serviceId);
        setIsModalOpen(true);
    };

    const handleClose = () => {
        setIsModalOpen(false);
        setEditingServiceId(null);
    };

    const handleServiceSuccess = async (createdService?: ServiceDetailsResponse) => {
        // #51: Optimistically show the newly-created service immediately so
        // the user gets visual confirmation without waiting for the refetch.
        if (createdService) {
            prependService(createdService);
        }
        await fetchData();
    };

    // Service delete handlers
    const handleDeleteServiceClick = (serviceId: number) => {
        const service = services.find(s => s.id === serviceId);
        if (service) {
            setItemToDelete({ type: 'service', item: service });
            setIsDeleteModalOpen(true);
        }
    };

    // Category menu and delete handlers
    const handleCategoryMenuClick = (categoryId: number) => {
        setOpenMenuId(openMenuId === categoryId ? null : categoryId);
    };

    const handleRemoveCategoryClick = (category: Category) => {
        setItemToDelete({ type: 'category', item: category });
        setIsDeleteModalOpen(true);
        setOpenMenuId(null);
    };

    const handleConfirmDelete = async () => {
        if (!itemToDelete) return;

        try {
            if (itemToDelete.type === 'service') {
                await deleteService(itemToDelete.item.id);
                showToast('Service deleted successfully', 'success');
                await fetchData();
            } else if (itemToDelete.type === 'category') {
                await removeCategoryFromBusiness(itemToDelete.item.id);
                showToast('Category removed successfully', 'success');
                await fetchCategories();
            }
        } catch (error) {
            console.error(`Error deleting ${itemToDelete.type}:`, error);
        } finally {
            setIsDeleteModalOpen(false);
            setItemToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteModalOpen(false);
        setItemToDelete(null);
    };

    const getDeleteModalTitle = () => {
        if (!itemToDelete) return '';
        return itemToDelete.type === 'service' ? 'Service' : 'Category';
    };

    const getDeleteModalName = () => {
        if (!itemToDelete) return '';
        return itemToDelete.item.name;
    };

    // #48: Live filter services & categories by search query
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const filteredServices = normalizedQuery
        ? services.filter(s =>
            s.name?.toLowerCase().includes(normalizedQuery) ||
            s.categoryName?.toLowerCase().includes(normalizedQuery)
        )
        : services;
    const filteredBusinessCategories = normalizedQuery
        ? businessCategories.filter(c => c.name?.toLowerCase().includes(normalizedQuery))
        : businessCategories;

    return (
        <div className={styles.servicesPage}>
            <div className={styles.controlsHeaderContainer}>
                <h1 className='xH1'>Services</h1>
                <div className={styles.controlsContainer}>
                    <ServiceControls />
                    {activeTap === "services" && (
                        <Button
                            label="Add service +"
                            variant="primary"
                            size="medium"
                            onClick={handleAddService}
                        />
                    )}
                </div>
            </div>
            <div className={styles.searchRow}>
                <div className={styles.searchSection}>
                    <SearchBar
                        icon={<img src="./assets/icons/magnifier.png" alt="magnifier" />}
                        placeholder="Search service name, category..."
                        value={searchQuery}
                        onChange={setSearchQuery}
                    />
                </div>
            </div>
            <div className={styles.tapsContainer}>
                <div className={styles.taps}>
                    <div
                        className={`${styles.tap} ${activeTap === 'services' ? styles.active : ''}`}
                        onClick={() => handleTapClick('services')}
                    >
                        Services
                    </div>
                    <div
                        className={`${styles.tap} ${activeTap === 'categories' ? styles.active : ''}`}
                        onClick={() => handleTapClick('categories')}
                    >
                        Categories
                    </div>
                </div>
                <div
                    className={`${styles.activeLine} ${activeTap === 'services' ? styles.servicesLine : styles.categoriesLine}`}
                />
            </div>

            {activeTap === "services" && (
                <div className={styles.servicesListContainer}>
                    {isLoading && (
                        <div className={styles.loadingOverlay}>
                            <div className={styles.spinner}>
                                <svg viewBox="0 0 50 50">
                                    <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                                </svg>
                                <span>Loading data...</span>
                            </div>
                        </div>
                    )}
                    <div className={styles.servicesList}>
                        {filteredServices.map((service) => (
                            <ServiceCard
                                key={service.id}
                                id={service.id}
                                title={service.name}
                                category={service.categoryName || ''}
                                duration={`${service.minDuration} - ${service.maxDuration}`}
                                branch={branchMap[Number(service.branchId)] || ''}
                                image={resolveImageUrl(service.imageUrl)}
                                pricingOptions={service.pricingOptions.map(option => ({
                                    option: option.name,
                                    price: option.price,
                                    duration: option.duration
                                }))}
                                onEdit={handleEditService}
                                onDelete={handleDeleteServiceClick}
                            />
                        ))}
                    </div>
                </div>
            )}

            {activeTap === "categories" && (
                <div className={styles.categoriesContainer}>
                    {/* CR-2026-05-15 — Soft-deprecate banner. Category management
                        has moved to My Business → Categories which has an
                        educational hero, a master-browse modal, and a friendlier
                        request-custom flow. This tab still works so existing
                        bookmarks don't 404, but the new home is the primary
                        path. Hard-redirect / full removal is a sibling decision. */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 14,
                            padding: '14px 18px',
                            background: 'var(--primary-subtle)',
                            border: '1px solid var(--primary-border)',
                            borderRadius: 12,
                            marginBottom: 18,
                            color: 'var(--text-primary)',
                        }}
                    >
                        <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }} aria-hidden="true">✨</span>
                        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>
                                Categories have a new home
                            </div>
                            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                We moved category management into My Business → Categories with a
                                clearer browse experience and an explainer of where each category
                                appears for customers.
                            </div>
                        </div>
                        <a
                            href="/my-business/categories"
                            style={{
                                background: 'var(--primary)',
                                color: 'var(--on-primary)',
                                padding: '9px 16px',
                                borderRadius: 999,
                                fontWeight: 700,
                                fontSize: 13,
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                            }}
                        >
                            Open new page →
                        </a>
                    </div>
                    {isLoading && (
                        <div className={styles.loadingOverlay}>
                            <div className={styles.spinner}>
                                <svg viewBox="0 0 50 50">
                                    <circle cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                                </svg>
                                <span>Loading data...</span>
                            </div>
                        </div>
                    )}
                    <div className={styles.addedCategories}>
                        <h3 className={styles.h3}>Added categories</h3>
                        <div className={styles.cardsContainer}>
                            {filteredBusinessCategories.map((category) => (
                                <div key={category.id} className={styles.card}>
                                    <div className={styles.categoryMenu} onClick={() => handleCategoryMenuClick(category.id)}>
                                        <svg className={styles.menuIcon} viewBox="0 0 6 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path style={{ fill: 'var(--text-tertiary)' }} d="M2.54902 5.1C1.07843 5.1 0 4 0 2.6C0 1.1 1.07843 0 2.54902 0C3.92157 0 5 1.1 5 2.6C5 4 3.92157 5.1 2.54902 5.1Z" />
                                            <path style={{ fill: 'var(--text-tertiary)' }} d="M2.54902 14.6C1.07843 14.6 0 13.4 0 12C0 10.6 1.07843 9.40002 2.54902 9.40002C3.92157 9.40002 5.09804 10.5 5.09804 12C5 13.4 3.92157 14.6 2.54902 14.6Z" />
                                            <path style={{ fill: 'var(--text-tertiary)' }} d="M2.54902 24C1.07843 24 0 22.9 0 21.4C0 20 1.07843 18.8 2.54902 18.8C3.92157 18.8 5.09804 19.9 5.09804 21.4C5 22.9 3.92157 24 2.54902 24Z" />
                                        </svg>
                                    </div>

                                    {openMenuId === category.id && (
                                        <div className={styles.categoryDropdownMenu} ref={menuRef}>
                                            <div className={styles.categoryDropdownItem} onClick={() => handleRemoveCategoryClick(category)}>
                                                <svg className={styles.icon} width="17" height="20" viewBox="0 0 17 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path style={{ stroke: 'var(--destructive-fg)' }} d="M14.4762 4.31384L13.5925 17.2745C13.5925 18.0109 13.0034 18.6 12.267 18.6H4.97663C4.24023 18.6 3.65111 18.0109 3.65111 17.2745L2.84106 4.31384" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path style={{ stroke: 'var(--destructive-fg)' }} d="M5.19751 4.24017L5.27115 2.1046C5.27115 1.51548 5.71299 1 6.15483 1H11.0887C11.6042 1 11.9724 1.51548 11.9724 2.1046L12.046 4.24017" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path style={{ stroke: 'var(--destructive-fg)' }} d="M1 4.24023H16.3172" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path style={{ stroke: 'var(--destructive-fg)' }} d="M5.71289 7.18579L6.22837 16.1699" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path style={{ stroke: 'var(--destructive-fg)' }} d="M8.21655 10.2051L8.51111 16.1699" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                <span>Delete</span>
                                            </div>
                                        </div>
                                    )}

                                    <img
                                        className={styles.img}
                                        src={resolveImageUrl(category.imageUrl)}
                                        alt={category.name}
                                        onError={handleImageError}
                                    />
                                    <span className={styles.categoryName}>{category.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Service Modal — category management moved to My Business → Categories */}
            {isModalOpen && activeTap === "services" && (
                <AddEditService
                    isOpen={isModalOpen}
                    onClose={handleClose}
                    onSuccess={handleServiceSuccess}
                    editingServiceId={editingServiceId}
                />
            )}

            {/* Delete Modal - only render when open to avoid phantom buttons (#1201) */}
            {isDeleteModalOpen && (<DeleteModal isOpen={isDeleteModalOpen} onClose={handleCancelDelete} onDelete={handleConfirmDelete} branchName={`${getDeleteModalTitle()}: ${getDeleteModalName()}`} />)}
        </div>
    );
};

export default Services;